// core
import React from "react";

// components
import Icon from "../../Icon/Icon";
import LSlider from "../LSlider/LSlider";

// styles
import cx from "classnames";
import styles from "./MonthlyCoverSlider.module.scss";
import IBaseFormComponentProps from "../../../interfaces/IBaseFormComponentProps";
import InputWrapper from "../InputWrapper/InputWrapper";
import Input from "../Input/Input";

interface ISliderPropsBase
    extends Omit<IBaseFormComponentProps<HTMLInputElement, number>, "onChange"> {
    id?: string;
    maxValue: number;
    step?: number;
    percentageRangeMax?: number;
    onChange: (e: number) => void;
    isRoundUp?: boolean;
}

interface ISliderPropsNoInput extends ISliderPropsBase {
    hasInput?: false;
}

interface ISliderPropsInput extends ISliderPropsBase {
    hasInput: true;
    isCurrency?: boolean;
}

type SliderProps = ISliderPropsInput | ISliderPropsNoInput;

const MonthlyCoverSlider: React.FC<SliderProps> = (props) => {
    const {
        name,
        label,
        value,
        onChange,
        onBlur,
        isDisabled,
        children,
        tooltipContent,
        hasError,
        isRoundUp,
        className
    } = props;
    const maxPercentage = props.percentageRangeMax || 1;
    const step = props.step || 50;
    const maxValue = props.maxValue || 0;

    const roundResult: (x: number) => number = isRoundUp ? Math.ceil : Math.floor;

    const hundredPercentValue = roundResult(maxValue / maxPercentage);

    const currentPercent = (value / hundredPercentValue) * 100;

    const sliderVals = `${
        Math.min(currentPercent || 0, maxPercentage * 100).toFixed(0) || "0"
    }% of $${Number(
        Math.floor(hundredPercentValue).toFixed(0)
    ).toLocaleString()} / month`;

    const handleIncrement = (val): void => {
        if (!isDisabled) {
            onChange(value + val);
        }
    };

    return (
        <InputWrapper
            className={cx(styles["slider-container"], { [className]: !!className })}
            hasError={hasError}
            label={label}>
            <div className="layout-flex-column">
                <span className="text-font-size-xs text-align--center">{sliderVals}</span>

                <div className={cx("layout-flex-row--centered")}>
                    <div
                        className={cx(styles["slider-controls"], {
                            [styles["disabled"]]: isDisabled
                        })}
                        onClick={() => handleIncrement(-step)}>
                        <Icon
                            id={isDisabled ? "SliderMinus" : "AccordionMinus"}
                            size="ml"
                        />
                    </div>

                    <LSlider
                        min={0}
                        max={maxValue}
                        value={value || 0}
                        disabled={isDisabled}
                        onChange={(e, number) => onChange(number[0] || number)}
                        step={step}
                        valueLabelDisplay="off"
                    />
                    <div
                        className={cx(styles["slider-controls"], {
                            [styles["disabled"]]: isDisabled
                        })}
                        onClick={() => handleIncrement(step)}>
                        <Icon
                            id={isDisabled ? "SliderPlus" : "AccordionPlus"}
                            size="ml"
                        />
                    </div>
                </div>
                {props.hasInput && (
                    <Input
                        name={name}
                        type="number"
                        value={value}
                        hasCurrency={props.isCurrency}
                        onChange={onChange}
                        onBlur={onBlur}
                        isDisabled={isDisabled}
                        tooltipContent={tooltipContent}
                        hasError={!!hasError}
                    />
                )}

                {children}
            </div>
        </InputWrapper>
    );
};

export default MonthlyCoverSlider;
